import {
  Component,
  inject,
  signal,
  computed,
  effect,
  ElementRef,
  viewChild,
  HostListener,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormsModule } from '@angular/forms';
import { FocusTrapModule } from 'primeng/focustrap';
import { Params, Router } from '@angular/router';
import { EMPTY, Subject, catchError, debounceTime, distinctUntilChanged, switchMap, of, map } from 'rxjs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { L10nPluralPipe } from '../../pipes/l10n-plural.pipe';
import { getSearchResultIcon } from '../../utils/icon-utils';
import { hasOpenPrimeDialog, isTypingTarget } from '../../utils/keyboard.utils';
import { claimEscape, isEscapeClaimed } from '@branduals/dialog-guard';
import { caughtErrorDetail, primaryCodeOf } from '@branduals/graphql-errors';
import { isUntitledAnnouncement, resolveResultUrl, searchModuleLabelKey } from '../../utils/search-result.utils';
import { CharCounterComponent } from '../char-counter/char-counter.component';
import {
  faMagnifyingGlass,
  faXmark,
  faClockRotateLeft,
  faArrowRight,
  faTrash
} from '@fortawesome/pro-duotone-svg-icons';

import { CommandPaletteService } from './command-palette.service';
import { SearchService, DisplaySearchResult, SearchQueryFilters, SEARCH_QUERY_MAX_LENGTH } from '../../services/search.service';
import { AnalyticsCollectorService } from '../../services/analytics-collector.service';
import { ItemType } from '../../models/item';

/**
 * A suggestion chip searches ONE type: the label is what the member sees (their language's word)
 * AND the query text the request carries, with the type as a filter beside it — a text-ranked
 * search restricted to the type, never a listing, so the results are ranked per language (the
 * en/de/it/fr labels also trip the server's type-word extraction, redundantly with the filter).
 */
export interface SuggestionChip {
  readonly labelKey: string;
  readonly type: ItemType;
}

/** One search the palette runs: the box's text plus the chip type it was started from, if any. */
interface PaletteSearch {
  readonly query: string;
  readonly type: ItemType | null;
}

@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [
    FormsModule,
    FocusTrapModule,
    FontAwesomeModule,
    TranslateModule,
    L10nPluralPipe,
    CharCounterComponent
  ],
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommandPaletteComponent {
  private commandPaletteService = inject(CommandPaletteService);
  private searchService = inject(SearchService);
  private analytics = inject(AnalyticsCollectorService);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  /** Emit-once guard: searchExecuted fires per new query text, not per keystroke burst. */
  private lastEmittedQuery: string | null = null;

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  readonly paletteElement = viewChild<ElementRef<HTMLDivElement>>('palette');

  // Icons
  faMagnifyingGlass = faMagnifyingGlass;
  faXmark = faXmark;
  faClockRotateLeft = faClockRotateLeft;
  faArrowRight = faArrowRight;
  faTrash = faTrash;

  // State
  isOpen = this.commandPaletteService.isOpen;
  /** The service's one opening guard — read BEFORE any preventDefault below. */
  private readonly canOpen = this.commandPaletteService.canOpen;
  recentSearches = this.commandPaletteService.recentSearches;
  hasRecentSearches = this.commandPaletteService.hasRecentSearches;

  searchQuery = signal('');
  results = signal<DisplaySearchResult[]>([]);
  isLoading = signal(false);
  selectedIndex = signal(0);
  private userNavigated = false;

  /**
   * The box stops at the server's search-query limit and says how much room is left as the
   * ceiling nears — a pasted paragraph is told the rule instead of being refused unannounced.
   */
  readonly searchQueryMaxLength = SEARCH_QUERY_MAX_LENGTH;
  readonly charsLeft = computed(() => SEARCH_QUERY_MAX_LENGTH - this.searchQuery().length);

  /**
   * The server's refusal of the CURRENT query, spoken in place of the empty state — a query can
   * still reach the server past the box's cap (a programmatic initial query), and "No results"
   * for a search that never ran would tell the member the brand has nothing. Cleared by the
   * next keystroke, Clear, and close.
   */
  readonly queryRefusal = signal<string | null>(null);

  /**
   * The chips shown when the box is empty and nothing was searched recently — each is a type
   * shortcut. The order is the order they render in.
   */
  readonly suggestionChips: readonly SuggestionChip[] = [
    { labelKey: 'search.suggestions.images', type: ItemType.Image },
    { labelKey: 'search.suggestions.videos', type: ItemType.Video },
    { labelKey: 'search.suggestions.campaigns', type: ItemType.Campaign },
    { labelKey: 'search.suggestions.articles', type: ItemType.Article },
  ];

  /**
   * The type filter the current search carries — set by a chip, dropped by the next keystroke:
   * the filter belongs to the chip's word, not to whatever the member types afterwards.
   * "See all results" reads it so the full page lists the same thing the palette did.
   */
  private readonly activeChipType = signal<ItemType | null>(null);

  // Autocomplete
  private suggestion = signal('');
  ghostText = computed(() => {
    const query = this.searchQuery();
    const s = this.suggestion();
    if (!s || !query) return '';
    // Show the full suggestion — input shows the typed part, ghost shows the rest
    const lower = s.toLowerCase();
    const queryLower = query.toLowerCase();
    if (lower.startsWith(queryLower)) {
      return query + s.slice(query.length);
    }
    return '';
  });

  // Search input subject for debouncing
  private searchInput$ = new Subject<PaletteSearch>();
  private suggestInput$ = new Subject<string>();
  /**
   * The last pair the palette actually SEARCHED — a settled search is not re-run by a no-op
   * keystroke, but the memory is the palette's own: Clear and close null it directly, so an
   * identical query afterwards searches again whatever the timing. (A reset pushed through the
   * debounced stream was coalesced away by a keystroke inside the window, and a stream operator
   * that remembered the last pair then swallowed the repeat — "No results found" for a search
   * that never ran.)
   */
  private lastSearched: PaletteSearch | null = null;

  // Computed
  hasQuery = computed(() => this.searchQuery().trim().length > 0);
  hasResults = computed(() => this.results().length > 0);
  showRecentSearches = computed(() => !this.hasQuery() && this.hasRecentSearches());
  showResults = computed(() => this.hasQuery() && this.hasResults());
  showEmptyState = computed(() => this.hasQuery() && !this.hasResults() && !this.isLoading() && !this.queryRefusal());
  totalResults = computed(() => this.results().length);

  // Items for keyboard navigation (recent searches or results)
  navigableItems = computed(() => {
    if (this.showRecentSearches()) {
      return this.recentSearches().map((q, i) => ({ type: 'recent' as const, value: q, index: i }));
    }
    if (this.showResults()) {
      return this.results().map((r, i) => ({ type: 'result' as const, value: r, index: i }));
    }
    return [];
  });

  constructor() {
    // Set up debounced search (the palette records its searches too — the query threads
    // through the pipe so query + resultCount land together at the subscribe seam)
    this.searchInput$.pipe(
      debounceTime(300),
      switchMap(({ query, type }) => {
        if (!query || query.trim().length === 0) {
          this.lastSearched = null;
          return of({ query: null as string | null, filters: undefined, results: [] as DisplaySearchResult[] });
        }
        // A repeat of the settled search is a no-op; a repeat while the same search is still in
        // flight re-issues it (switching would otherwise cancel the pending request into silence).
        if (this.lastSearched?.query === query && this.lastSearched.type === type && !this.isLoading()) {
          return EMPTY;
        }
        this.lastSearched = { query, type };
        this.isLoading.set(true);
        const filters: SearchQueryFilters | undefined = type === null ? undefined : { types: [type] };
        // Over-fetch then show 8: the server trims AFTER ranking, so a page fetched at exactly
        // 8 can render fewer (or zero) rows even when the member's own item matched — the
        // workflow corpus makes trimmed shares material on brands with similarly-named objects.
        return this.searchService.query(query, 24, filters).pipe(
          map(r => ({ query: query as string | null, filters, results: r.results.slice(0, 8) })),
          // Palette-local swallow: query() propagates errors for the search page; the palette
          // keeps its silent-empty behavior AND its long-lived stream alive — except the
          // server's REFUSAL of the query itself, which it speaks (the sentence names the
          // limit) instead of claiming the brand has nothing matching.
          catchError(err => {
            if (primaryCodeOf(err) === 'SEARCH_QUERY_TOO_LONG') {
              this.queryRefusal.set(caughtErrorDetail(
                err, (k, p) => this.translateService.instant(k, p), 'search.error.description'));
            }
            return of({ query: query as string | null, filters, results: [] as DisplaySearchResult[] });
          })
        );
      }),
      takeUntilDestroyed()
    ).subscribe(({ query, filters, results }) => {
      this.results.set(results);
      this.isLoading.set(false);
      this.selectedIndex.set(0);
      // Emit-once per query text — the debounced pipe already coalesces keystrokes.
      if (query && query !== this.lastEmittedQuery) {
        this.lastEmittedQuery = query;
        this.analytics.searchExecuted(query, results.length, filters as Record<string, unknown> | undefined);
      }
    });

    // Set up autocomplete suggestions (faster debounce)
    this.suggestInput$.pipe(
      debounceTime(150),
      distinctUntilChanged(),
      switchMap(query => {
        if (!query || query.trim().length < 2) {
          return of([] as string[]);
        }
        return this.searchService.suggest(query, 1);
      }),
      takeUntilDestroyed()
    ).subscribe(suggestions => {
      this.suggestion.set(suggestions[0] ?? '');
    });

    // Focus input when palette opens. The palette is a dialog by its own markup: pFocusTrap keeps
    // Tab inside it (the slide panel's shape — Tab past its last control walked into the grid
    // behind it, where Enter opened a preview underneath the palette), and the element that
    // invoked it takes focus back on close (focus-return is not part of the directive).
    effect(() => {
      if (this.isOpen()) {
        this.previouslyFocused = (document.activeElement as HTMLElement | null) ?? null;
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          this.searchInput()?.nativeElement?.focus();
          // Set initial query if provided
          const initialQuery = this.commandPaletteService.initialQuery();
          if (initialQuery) {
            this.searchQuery.set(initialQuery);
            this.onSearchChange(initialQuery);
          }
        }, 50);
      } else {
        // Reset state when closed — the search MEMORY too, directly (see lastSearched): the same
        // search on reopen must run again. The empty emission clears the rows through the pipe's
        // own no-request branch.
        this.searchQuery.set('');
        this.results.set([]);
        this.selectedIndex.set(0);
        this.suggestion.set('');
        this.activeChipType.set(null);
        this.queryRefusal.set(null);
        this.lastSearched = null;
        this.searchInput$.next({ query: '', type: null });
        const prev = this.previouslyFocused;
        this.previouslyFocused = null;
        if (prev && document.contains(prev)) prev.focus();
      }
    });
  }

  /** The element that invoked the palette — focus returns to it on close. */
  private previouslyFocused: HTMLElement | null = null;


  /**
   * Global keyboard listener for opening/closing the palette
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // A PrimeNG dialog open above owns the keyboard (the file preview's stand-down shape): the
    // hotkeys neither open nor close the palette under it, and Escape belongs to the dialog.
    const dialogAbove = hasOpenPrimeDialog();

    // Cmd/Ctrl + K to toggle. When the palette may not open, the key is left to the browser
    // (its own shortcut survives) — but an OPEN palette can always be closed by it.
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      if (dialogAbove) return;
      if (!this.isOpen() && !this.canOpen()) return;
      event.preventDefault();
      this.commandPaletteService.toggle();
      return;
    }

    // "/" to open (when not in input). A person the chrome withdrew from keeps the browser's
    // own "/" quick-find — the guard runs BEFORE preventDefault.
    if (event.key === '/' && !isTypingTarget(event) && !this.isOpen()) {
      if (dialogAbove || !this.canOpen()) return;
      event.preventDefault();
      this.commandPaletteService.open();
      return;
    }

    // Only handle these if palette is open
    if (!this.isOpen()) return;

    // Escape closes — one rung of the document's Escape ladder: it stands down on a press another
    // rung already claimed (a dialog above), and when it acts it CLAIMS the press and stops it
    // here, so the rung beneath (a slide panel, a preview) never closes on the same Escape.
    if (event.key === 'Escape') {
      if (dialogAbove || event.isComposing || isEscapeClaimed(event)) return;
      this.commandPaletteService.close();
      claimEscape(event);
      event.stopImmediatePropagation();
      return;
    }

    // Tab to accept autocomplete suggestion
    if (event.key === 'Tab' && this.ghostText()) {
      event.preventDefault();
      this.acceptSuggestion();
      return;
    }

    // Right arrow at end of input to accept autocomplete suggestion
    if (event.key === 'ArrowRight' && this.ghostText()) {
      const input = this.searchInput()?.nativeElement;
      if (input && input.selectionStart === input.value.length) {
        event.preventDefault();
        this.acceptSuggestion();
        return;
      }
    }

    // Arrow navigation
    const items = this.navigableItems();
    if (items.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.userNavigated = true;
      this.selectedIndex.update(i => Math.min(i + 1, items.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.userNavigated = true;
      this.selectedIndex.update(i => Math.max(i - 1, 0));
      return;
    }

    // Enter to search or select
    if (event.key === 'Enter') {
      event.preventDefault();
      const items = this.navigableItems();
      const selected = this.userNavigated ? items[this.selectedIndex()] : undefined;
      if (selected) {
        if (selected.type === 'recent') {
          this.selectRecentSearch(selected.value as string);
        } else {
          this.selectResult(selected.value as DisplaySearchResult, event.metaKey || event.ctrlKey);
        }
      } else if (this.hasQuery()) {
        this.goToFullResults();
      }
      return;
    }
  }

  /**
   * A suggestion chip searches its type: the box shows the word the member sees — the label in
   * the interface language, resolved at click time so a language switch is honoured — and the
   * request carries that SAME word as its query text beside the chip's type filter. Both arms are
   * a text-ranked search restricted to the type, not a listing; where the label happens to be a
   * type word the engine recognises (the English, German, Italian and French labels are), the
   * server's own query understanding adds the same type clause a second time, redundantly. No
   * autocomplete ghost: a chip is a whole query, not a prefix being typed.
   */
  searchSuggestion(chip: SuggestionChip): void {
    const label = this.translateService.instant(chip.labelKey);
    this.searchQuery.set(label);
    this.activeChipType.set(chip.type);
    this.suggestion.set('');
    this.searchInput$.next({ query: label, type: chip.type });
    this.selectedIndex.set(0);
    this.userNavigated = false;
  }

  /**
   * Handle search input changes — a keystroke is a plain search; any chip filter is dropped.
   */
  onSearchChange(query: string): void {
    this.searchQuery.set(query);
    this.activeChipType.set(null);
    this.queryRefusal.set(null); // a fresh query — the previous refusal was about the previous text
    this.searchInput$.next({ query, type: null });
    this.suggestInput$.next(query);
    this.selectedIndex.set(0);
    this.userNavigated = false;
  }

  /**
   * Accept the current autocomplete suggestion
   */
  private acceptSuggestion(): void {
    const ghost = this.ghostText();
    if (!ghost) return;
    this.searchQuery.set(ghost);
    this.suggestion.set('');
    this.activeChipType.set(null);
    this.searchInput$.next({ query: ghost, type: null });
    this.suggestInput$.next(ghost);
    // Move cursor to end
    const input = this.searchInput()?.nativeElement;
    if (input) {
      input.value = ghost;
      input.setSelectionRange(ghost.length, ghost.length);
    }
  }

  /**
   * Clear the search input
   */
  clearSearch(): void {
    this.searchQuery.set('');
    this.results.set([]);
    this.selectedIndex.set(0);
    this.activeChipType.set(null);
    this.queryRefusal.set(null);
    // Reset the search memory directly (see lastSearched): retyping the same word must search again.
    this.lastSearched = null;
    this.searchInput$.next({ query: '', type: null });
    this.searchInput()?.nativeElement?.focus();
  }

  /**
   * Handle backdrop click to close
   */
  onBackdropClick(event: MouseEvent): void {
    // Only close if clicking on the backdrop itself, not the palette
    if (event.target === event.currentTarget) {
      this.commandPaletteService.close();
    }
  }

  /**
   * Select a recent search
   */
  selectRecentSearch(query: string): void {
    this.searchQuery.set(query);
    this.onSearchChange(query);
  }

  /**
   * Remove a recent search
   */
  removeRecentSearch(event: MouseEvent, query: string): void {
    event.stopPropagation();
    this.commandPaletteService.removeRecentSearch(query);
  }

  /**
   * Clear all recent searches
   */
  clearAllRecentSearches(): void {
    this.commandPaletteService.clearRecentSearches();
  }

  /**
   * Select a search result
   */
  selectResult(result: DisplaySearchResult, openInNewTab: boolean = false): void {
    // Search conversion: palette opens were invisible to analytics before this event.
    this.analytics.searchResultClicked(this.searchQuery(), this.results().indexOf(result), result.id, result.type);

    // Add to recent searches
    this.commandPaletteService.addRecentSearch(this.searchQuery());

    // Close palette
    this.commandPaletteService.close();

    // Navigate to the result
    const url = this.getResultUrl(result);
    if (openInNewTab) {
      window.open(url, '_blank');
    } else {
      this.router.navigateByUrl(url);
    }
  }

  /**
   * Navigate to full search results page
   */
  goToFullResults(): void {
    const query = this.searchQuery().trim();
    if (!query) return;

    // Add to recent searches
    this.commandPaletteService.addRecentSearch(query);

    // Close palette
    this.commandPaletteService.close();

    // Navigate to the search page; a chip's type travels with the word so the full page lists
    // the same thing the palette did (the page reads `types` as the numeric enum values).
    const queryParams: Params = { q: query };
    const type = this.activeChipType();
    if (type !== null) {
      queryParams['types'] = String(type);
    }
    this.router.navigate(['/search'], { queryParams });
  }

  /**
   * Get the navigation URL for a result (derives from the ONE canonical nav map,
   * utils/url.utils.buildItemUrlFromParts; the old hand-rolled copy diverged from the search
   * page's map, and its `parentSlug` branch was dead — the service never populates it, so
   * every file click landed on a bare module root instead of the item).
   */
  private getResultUrl(result: DisplaySearchResult): string {
    return resolveResultUrl(result, this.searchQuery());
  }

  /** The type glyph — the search surfaces' one icon home (the result card renders the same). */
  getResultIcon(result: DisplaySearchResult) {
    return getSearchResultIcon(result.type);
  }

  /** Module label key for the row's path line, or null when the wire carried no known module. */
  getModuleLabelKey(result: DisplaySearchResult): string | null {
    return searchModuleLabelKey(result.module);
  }

  /** The shared untitled predicate (whitespace-widened) — one rule for card, palette and Hub. */
  isUntitledAnnouncement(result: DisplaySearchResult): boolean {
    return isUntitledAnnouncement(result.type, result.displayTitle);
  }

  /**
   * Get keyboard shortcut display
   */
  getShortcutDisplay(): string {
    const isMac = (navigator as any).userAgentData?.platform?.toLowerCase().includes('mac') ?? /Mac|iPhone|iPad/.test(navigator.userAgent);
    return isMac ? '⌘K' : 'Ctrl+K';
  }
}
